import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import MobileLayout from '../../components/mobile/MobileLayout';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Home, Loader2, FileText, Calendar, Building2,
  MapPin, Search, ChevronRight, StickyNote,
} from 'lucide-react';
import { format } from 'date-fns';

const MobileMyDockets = () => {
  const navigate = useNavigate();
  const { api } = useMobileAuth();
  const [dockets, setDockets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchDockets = async () => {
      try {
        const res = await api.get('/api/mobile/my-dockets');
        setDockets(res.data || []);
      } catch (err) {
        console.error('Failed to fetch dockets:', err);
        toast.error('Failed to load your dockets');
      } finally {
        setLoading(false);
      }
    };
    fetchDockets();
  }, [api]);

  const filtered = dockets.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (d.docket_number || '').toLowerCase().includes(q) ||
      (d.customer_name || '').toLowerCase().includes(q) ||
      (d.site_name || '').toLowerCase().includes(q) ||
      (d.job_description || '').toLowerCase().includes(q) ||
      (d.job_date || '').includes(q)
    );
  });

  const formatDate = (d) => {
    try { return format(new Date(d + 'T00:00:00'), 'dd MMM yyyy'); }
    catch { return d; }
  };

  const getStatusColor = (s) => {
    if (s === 'approved' || s === 'pending_approval') return 'bg-green-500/20 text-green-400';
    if (s === 'draft') return 'bg-amber-500/20 text-amber-400';
    return 'bg-gray-500/20 text-[var(--m-text-secondary)]';
  };

  return (
    <MobileLayout>
      <div className="min-h-screen bg-[var(--m-bg)]" data-testid="my-dockets-screen">
        {/* Header */}
        <div className="bg-[var(--m-bg)] border-b border-[var(--m-border)] px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text)]" data-testid="my-dockets-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-[var(--m-text)] font-semibold">My Dockets</h1>
          </div>
          <button onClick={() => navigate('/mobile/dashboard')} className="text-[var(--m-text-secondary)]">
            <Home className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 pt-3 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--m-text-muted)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search dockets..."
              className="w-full pl-9 pr-4 py-2.5 bg-[var(--m-bg-card)] border border-[var(--m-border)] rounded-lg text-[var(--m-text)] text-sm placeholder:text-[var(--m-text-muted)] focus:outline-none focus:ring-1 focus:ring-[#ffd108]"
              data-testid="my-dockets-search"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center px-4">
            <FileText className="h-12 w-12 text-[var(--m-text-muted)] mb-3" />
            <p className="text-[var(--m-text-secondary)]">{search ? 'No dockets match your search' : 'No dockets submitted yet'}</p>
          </div>
        ) : (
          <div className="px-4 pb-24 space-y-2 pt-1">
            {filtered.map((docket) => {
              const noteCount = (docket.post_submission_notes || []).length;
              return (
                <button
                  key={docket.id}
                  onClick={() => navigate(`/mobile/my-dockets/${docket.id}`)}
                  className="w-full p-3 bg-[var(--m-bg-card)] rounded-lg text-left hover:bg-[var(--m-bg-elevated)] transition-colors"
                  data-testid={`docket-card-${docket.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[var(--m-text)] font-medium text-sm">
                          #{docket.docket_number || '—'}
                        </span>
                        <Badge className={`text-[10px] px-1.5 py-0 ${getStatusColor(docket.status)}`}>
                          {(docket.status || 'draft').replace('_', ' ')}
                        </Badge>
                        {noteCount > 0 && (
                          <span className="flex items-center gap-0.5 text-[10px] text-amber-400">
                            <StickyNote className="h-3 w-3" /> {noteCount}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-[var(--m-text-secondary)] mb-0.5">
                        <Calendar className="h-3 w-3" />
                        <span>{formatDate(docket.job_date)}</span>
                      </div>
                      {docket.customer_name && (
                        <div className="flex items-center gap-1 text-xs text-[var(--m-text-muted)]">
                          <Building2 className="h-3 w-3" />
                          <span className="truncate">{docket.customer_name}</span>
                          {docket.site_name && (
                            <>
                              <MapPin className="h-3 w-3 ml-1" />
                              <span className="truncate">{docket.site_name}</span>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-[var(--m-text-secondary)] flex-shrink-0 mt-1" />
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </MobileLayout>
  );
};

export default MobileMyDockets;
